'use client';

import { useCallback, useId, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';

import { Button } from '../../button';
import { Icon } from '../../icon';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/Popover';
import { FieldDropdownIcon } from '../layout/FieldDropdownIcon';
import { getButtonVariant } from '../layout/SelectOption';
import type { SelectOption } from '../layout/SelectOption.types';
import { SelectOptions } from '../layout/SelectOptions';
import type { SelectDropdownProps } from './SelectDropdown.types';

import styles from './SelectDropdown.module.scss';

export function SelectDropdown({
  style,
  className,
  onSelect,
  options,
  disabled = false,
  error = false,
  placeholder = 'Select...',
  testId = 'ui-select-dropdown',
}: SelectDropdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownId = useId();
  const listboxId = `${dropdownId}-listbox`;
  const buttonId = `${dropdownId}-button`;

  const selectedOption = useMemo(
    () => options.find((option) => option.selected),
    [options],
  );

  const selectedLabel = selectedOption?.label ?? placeholder;

  const availableOptions = useMemo(
    () => options.filter((option) => !option.disabled),
    [options],
  );

  const dispatchChangeEvent = useCallback(() => {
    containerRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
  }, []);

  const handleOptionSelect = useCallback(
    (option: SelectOption) => {
      if (option.disabled) return;

      onSelect(option.value);
      setIsOpen(false);
      dispatchChangeEvent();

      setTimeout(() => {
        buttonRef.current?.focus();
      }, 0);
    },
    [onSelect, dispatchChangeEvent],
  );

  const handleButtonClick = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleDropdownIconClick = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => {
        if (!prev) buttonRef.current?.focus();
        return !prev;
      });
    }
  }, [disabled]);

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottom-start"
      matchReferenceWidth
      dismissOnAncestorScroll
    >
      <div
        ref={containerRef}
        style={style}
        className={classnames(styles['select-dropdown'], className)}
        data-testid={testId}
      >
        <PopoverTrigger asChild>
          <Button
            ref={buttonRef}
            id={buttonId}
            variant={getButtonVariant(error)}
            className={styles['button']}
            tabIndex={disabled ? -1 : 0}
            onClick={handleButtonClick}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-label={selectedLabel}
          >
            <span
              className={classnames(
                styles['label'],
                selectedLabel === placeholder && styles['placeholder'],
              )}
            >
              {selectedOption?.icon && (
                <Icon icon={selectedOption.icon} size="sm" />
              )}
              {selectedLabel}
            </span>
          </Button>
        </PopoverTrigger>
        <FieldDropdownIcon
          disabled={disabled}
          error={error}
          onClick={handleDropdownIconClick}
        />
      </div>
      {isOpen && availableOptions.length > 0 && (
        <PopoverContent style={{ padding: 0 }}>
          <SelectOptions
            testId={`${testId}_options`}
            options={options}
            onClick={handleOptionSelect}
            id={listboxId}
            role="listbox"
            aria-labelledby={buttonId}
          />
        </PopoverContent>
      )}
    </Popover>
  );
}
